import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { superAdminApi } from '../services/super-admin-api'

export interface MetaWhatsAppSecretStatus {
  configured: boolean
  masked: string | null
}

export interface MetaWhatsAppConfiguration {
  provider: string
  graphApiVersion: string
  configured: boolean
  missingConfig: string[]
  accessToken: MetaWhatsAppSecretStatus
  appSecret: MetaWhatsAppSecretStatus
  webhookVerifyToken: MetaWhatsAppSecretStatus
  businessAccountId: string | null
  phoneNumberId: string | null
  webhookCallbackPath: string
  subscribedWebhookField: string
  signatureVerificationEnabled: boolean
}

export interface MetaWhatsAppBusinessAccount {
  id: string
  name?: string
  currency?: string
  timezone_id?: string
  message_template_namespace?: string
}

export interface MetaWhatsAppPhoneNumber {
  id: string
  display_phone_number?: string
  verified_name?: string
  code_verification_status?: string
  quality_rating?: string
  platform_type?: string
  throughput?: {
    level?: string
  }
  name_status?: string
}

export interface MetaWhatsAppBusinessProfile {
  about?: string
  address?: string
  description?: string
  email?: string
  profile_picture_url?: string
  websites?: string[]
  vertical?: string
  messaging_product?: string
}

export interface MetaWhatsAppSubscribedApp {
  id: string | null
  name: string | null
  link: string | null
}

export interface MetaWhatsAppTemplateComponent {
  type: string
  text?: string
  format?: string
  example?: unknown
}

export interface MetaWhatsAppTemplate {
  id: string
  name: string
  status: string
  category: string
  language: string
  rejected_reason?: string
  quality_score?: {
    score?: string
    date?: number
  }
  components?: MetaWhatsAppTemplateComponent[]
}

export interface MetaWhatsAppStatus {
  lastCheckedAt: string
  configuration: MetaWhatsAppConfiguration
  businessAccount: MetaWhatsAppBusinessAccount | null
  phoneNumber: MetaWhatsAppPhoneNumber | null
  businessProfile: MetaWhatsAppBusinessProfile | null
  subscribedApps: MetaWhatsAppSubscribedApp[]
  templates: MetaWhatsAppTemplate[]
  templateSummary: Record<string, number>
  activeAlerts: MetaWhatsAppAlert[]
  metaErrors: string[]
}

export interface MetaWhatsAppAlert {
  key: string
  label: string
  status: 'active' | 'pending_template' | 'disabled'
  channel: string
  trigger: string
  delivery: string
}

export interface MetaWhatsAppSpendCategory {
  category: string
  conversations: number
  rateBrl: number
  estimatedCostBrl: number
}

export interface MetaWhatsAppMetrics {
  period: {
    days: number
    since: string
    until: string
  }
  totals: {
    receivedMessages: number
    sentMessages: number
    statusEvents: number
    whatsappReportsReceived: number
    activeConversationWindows: number
    notificationsQueued: number
    notificationsSent: number
    notificationsDelivered: number
    notificationsFailed: number
  }
  byNotificationType: Record<string, number>
  byDeliveryStatus: Record<string, number>
  byMessageDirection: Record<string, number>
  spend: {
    estimatedCostBrl: number
    ratesConfigured: boolean
    currency: string
    byCategory: MetaWhatsAppSpendCategory[]
    note: string
  }
  activeAlerts: MetaWhatsAppAlert[]
}

export interface TemplateSubmissionResult {
  name: string
  language: string
  success: boolean
  skipped?: boolean
  id?: string
  status?: string
  category?: string
  message?: string
  metaError?: string
}

export interface TemplateSubmissionResponse {
  success: boolean
  submitted: number
  skipped: number
  failed: number
  results: TemplateSubmissionResult[]
}

export function useMetaWhatsAppStatus() {
  return useQuery<MetaWhatsAppStatus>({
    queryKey: ['super-admin', 'meta-whatsapp', 'status'],
    queryFn: () => superAdminApi.get<MetaWhatsAppStatus>('/meta-whatsapp/status'),
    staleTime: 30_000,
  })
}

export function useMetaWhatsAppMetrics(days = 30) {
  return useQuery<MetaWhatsAppMetrics>({
    queryKey: ['super-admin', 'meta-whatsapp', 'metrics', days],
    queryFn: () => superAdminApi.get<MetaWhatsAppMetrics>('/meta-whatsapp/metrics', { days }),
    staleTime: 30_000,
  })
}

export function useSubmitDefaultMetaWhatsAppTemplates() {
  const queryClient = useQueryClient()

  return useMutation<TemplateSubmissionResponse>({
    mutationFn: () =>
      superAdminApi.post<TemplateSubmissionResponse>('/meta-whatsapp/templates/submit-defaults'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'meta-whatsapp'] })
    },
  })
}
